import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AppButtonComponent } from 'src/app/shared/components/app-button/app-button.component';
import { AppInputCustomComponent, InputMaskTypes } from 'src/app/shared/components/app-input-custom/app-input-custom.component';
import { Location } from '@angular/common';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { AlertService, AlertType } from 'src/app/core/services/alert.service';
import { ReplaceTextPipe } from 'src/app/shared/pipe/replace-text.pipe';
import { PaymentDataService } from 'src/app/core/services/payment-data.service';

@Component({
  selector: 'app-adicionar-cartao',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AppInputCustomComponent, AppButtonComponent],
  templateUrl: './adicionar-cartao.component.html',
  styleUrl: './adicionar-cartao.component.scss'
})
export class AdicionarCartaoComponent implements OnInit {
  InputMaskTypesEnum = InputMaskTypes;
  labelColor = 'white';
  title = 'Adicionar cartão';
  addCreditCardformGroup = new FormGroup({
    cardNumberControl: new FormControl<string>('', [
      Validators.required,
      Validators.pattern(/^\d{16}$/) // Número do cartão deve ter 16 dígitos
    ]),
    expirationDateControl: new FormControl<string>('', [
      Validators.required,
      Validators.pattern(/^(0[1-9]|1[0-2])\/\d{4}$/) // Formato MM/AAAA
    ]),
    cvvControl: new FormControl<string>('', [
      Validators.required,
      Validators.pattern(/^\d{3}$/) // CVV deve ter 3 dígitos
    ]),
    cardNameControl: new FormControl<string>('', [
      Validators.required,
      Validators.minLength(3) // Nome do titular deve ter pelo menos 3 caracteres
    ]),
    CPFControl: new FormControl<string>('', [
      Validators.required,
      Validators.pattern(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/) // Formato CPF: 000.000.000-00
    ])
  });
  btnSaveOptions = {
    buttonText: 'Salvar',
    borderRadius: '25px',
    buttonSize: 'btn btn-sm py-2 px-5 btn-primary',
    btnDisabledBackground: '#f2e2b2',
    btnDisabledTextColor: '#000000'
  };
  btnCancelOptions = {
    buttonText: 'Cancelar',
    borderRadius: '25px',
    buttonSize: 'btn btn-sm py-2 px-5 btn-secondary',
  };

  constructor(
    private location: Location,
    private alertService: AlertService,
    private paymentDataService: PaymentDataService,
    private replaceTextPipe: ReplaceTextPipe
  ) { }

  ngOnInit(): void { }

  applyExpirationDateMask(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    let value = inputElement.value.replace(/\D/g, ''); // Remove caracteres não numéricos

    if (value.length > 2) {
      value = value.substring(0, 2) + '/' + value.substring(2, 6); // Insere a barra após os dois primeiros caracteres
    }

    if (value.length > 7) {
      value = value.substring(0, 7); // Limita o tamanho máximo para MM/AAAA
    }

    inputElement.value = value;
    this.addCreditCardformGroup.get('expirationDateControl')?.setValue(value);
  }

  save(): void {
    const currentUser = localStorage.getItem('current-user');

    if (!currentUser) {
      this.showAlert(AlertType.DANGER, 'Usuário não encontrado no localStorage.');
      return;
    }

    const parsedUser = JSON.parse(currentUser);
    const memberId = parsedUser?.id;

    if (!memberId) {
      this.showAlert(
        AlertType.DANGER,
        this.replaceTextPipe.transform('ID do sócio não encontrado.')
      );
      return;
    }

    const cardExpirationDate = this.addCreditCardformGroup.get('expirationDateControl')?.value?.trim();

    if (!cardExpirationDate || !/^(0[1-9]|1[0-2])\/\d{4}$/.test(cardExpirationDate)) {
      this.showAlert(AlertType.DANGER, 'Data de validade inválida. Deve estar no formato MM/AAAA.');
      return;
    }

    const [month, year] = cardExpirationDate.split('/');
    const monthNumber = parseInt(month, 10);
    const yearNumber = parseInt(year, 10);

    // Valida se o mês está entre 1 e 12
    if (monthNumber < 1 || monthNumber > 12) {
      this.showAlert(AlertType.DANGER, 'Mês inválido. Deve estar entre 01 e 12.');
      return;
    }

    const currentYear = new Date().getFullYear();
    const maxYear = currentYear + 20; // Define o limite máximo para o ano

    // Valida se o ano não ultrapassa o limite
    if (yearNumber > maxYear) {
      this.showAlert(AlertType.DANGER, `Ano inválido. O ano não pode ser maior que ${maxYear}.`);
      return;
    }

    const expirationDate = new Date(yearNumber, monthNumber - 1);
    const currentDate = new Date();
    currentDate.setDate(1); // Define o dia como 1 para comparar apenas mês e ano

    // Valida se a data de validade não é menor que o mês atual
    if (expirationDate < currentDate) {
      this.showAlert(AlertType.DANGER, 'Insira uma Data válida de validade do cartão.');
      return;
    }

    const cardNumber = this.addCreditCardformGroup.get('cardNumberControl')?.value?.trim();
    const cardCvv = this.addCreditCardformGroup.get('cvvControl')?.value?.trim();
    const cardHolderName = this.addCreditCardformGroup.get('cardNameControl')?.value?.trim();
    const cpf = this.addCreditCardformGroup.get('CPFControl')?.value?.trim();

    if (!cardNumber || cardNumber.length < 16) {
      this.showAlert(AlertType.DANGER, 'Número do cartão inválido.');
      return;
    }

    if (!cardCvv || !/^\d{3,4}$/.test(cardCvv)) {
      this.showAlert(AlertType.DANGER, 'CVV inválido. Deve conter 3 ou 4 dígitos.');
      return;
    }

    if (!cardHolderName || cardHolderName.trim().length === 0) {
      this.showAlert(AlertType.DANGER, 'Nome do cartão inválido.');
      return;
    }


    const newCard = {
      cardNumber,
      cardExpirationDate,
      cardCvv,
      cardHolderName,
      cpf,
      memberId
    };

    this.paymentDataService.changeCard(newCard).subscribe({
      next: (response) => {
        this.showAlert(AlertType.SUCCESS, 'Cartão adicionado com sucesso!');
        setTimeout(() => {
          this.location.back();
        }, 2000);
      },
      error: (error) => {
        const errorMessage = error.error?.message || 'Erro ao alterar cartão. Por favor, tente novamente.';
        this.showAlert(AlertType.DANGER, errorMessage);
      }
    });
  }

  cancel(): void {
    this.location.back();
  }

  showAlert(type: AlertType, message: string): void {
    this.alertService.showAlert(type, message);
  }
}